import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import Button from 'reactstrap/lib/Button';
import { withCoreComponent, withModalActions, withLocale } from 'core/hocs';
import { GamesFilters as GamesFiltersCore, FormattedTag, ChangeRoute } from 'core/components';
import { GAME_CATEGORY_FILTER } from 'core/constants';
import { isEmptyOrNil, setDocumentScrollTopPosition } from 'core/helpers';

import { switchLanguageSymbols } from 'helpers/games-search';
import { GameCard } from 'components/game-card/game-card';
import { SubcategoryGamesCarousel } from 'components/subcategory-games/subcategory-games-carousel';
import { ProducersModal } from 'components/modals/producers-modal/producers-modal';
import { IconGamesNotFoundWithMark } from 'components/icons/icon-games-not-found-with-mark/icon-games-not-found-with-mark';
import { GamesFiltersMenu } from './games-filters-menu';
import { GamesSearchInput } from './games-search-input';
import { Producers } from './producers/producers';
import { MODAL_IDS, SUBCATEGORIES_BY_PAGE } from '../../constants';

import './games-filters.scss';

const GAMES_SEARCH_MOBILE_OPEN = 'games-search-mobile-open';

class GamesFiltersUI extends Component {
  static propTypes = {
    isUserLoggedIn: PropTypes.bool.isRequired,
    openGame: PropTypes.func.isRequired,
    toggleFavoriteGame: PropTypes.func.isRequired,
    setSearchValue: PropTypes.func.isRequired,
    setProducers: PropTypes.func.isRequired,
    clearAllFilters: PropTypes.func.isRequired,
    games: PropTypes.arrayOf(PropTypes.shape),
    filteredByCategoryGames: PropTypes.arrayOf(PropTypes.shape),
    producers: PropTypes.arrayOf(PropTypes.string),
    filters: PropTypes.shape({
      category: PropTypes.string,
      searchValue: PropTypes.string,
      producers: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    showMore: PropTypes.func.isRequired,
    isShowMoreAvailable: PropTypes.bool.isRequired,
    gamesFound: PropTypes.number.isRequired,
    jackpotsIsInProgress: PropTypes.bool.isRequired,
    favoriteGamesIsInProgress: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    history: PropTypes.shape().isRequired,
    locale: PropTypes.string.isRequired,
    currentModal: PropTypes.shape({
      id: PropTypes.string,
      data: PropTypes.any,
    }).isRequired,
  };

  static defaultProps = {
    games: [],
    filteredByCategoryGames: [],
    producers: [],
  };

  state = {
    isOpenSearch: false,
    isOverlayOpen: false,
    searchValue: '',
  };

  componentDidUpdate = () => {
    const { isOverlayOpen } = this.state;

    if (isOverlayOpen) {
      setDocumentScrollTopPosition();
    }

    if (!isOverlayOpen) {
      document.body.classList.remove(GAMES_SEARCH_MOBILE_OPEN);
    }
  }

  componentWillUnmount = () => {
    const { isOverlayOpen } = this.state;

    if (isOverlayOpen) {
      document.body.classList.remove(GAMES_SEARCH_MOBILE_OPEN);
    }
  }

  setProducers = ({ currentTarget: { dataset: { producer } } }) => {
    const { filters: { producers }, setProducers } = this.props;

    const newProducers = R.ifElse(
      R.includes(producer),
      R.without([producer]),
      R.append(producer),
    )(producers);

    setProducers(newProducers);
  };

  setAllProducers = () => {
    const {
      producers,
      setProducers,
      filters: { producers: filterProducers },
    } = this.props;

    if (producers.length === filterProducers.length) {
      return setProducers([]);
    }

    return setProducers(producers);
  }

  openProducersModal = () => {
    const { openModal } = this.props;

    openModal(MODAL_IDS.PRODUCERS);
  };

  onChange = (e) => {
    const { value } = e.target;
    const { setSearchValue } = this.props;
    const { switchedValue, defaultValue } = switchLanguageSymbols(value);

    setSearchValue(switchedValue);

    this.setState({ searchValue: defaultValue });
  };

  clearProducers = (e) => {
    const { setProducers } = this.props;

    e.stopPropagation();
    setProducers([]);
  };

  clearSearchValue = () => {
    const { setSearchValue } = this.props;

    setSearchValue('');
    this.setState({ searchValue: '' });

    this.onOverLayClose();
    this.toggleSearchInput();
  };

  toggleSearchInput = () => (
    this.setState(prevState => ({
      isOpenSearch: !prevState.isOpenSearch,
    }))
  );

  onOverlayOpen = () =>
    this.setState(({ isOverlayOpen: true }), () => document.body.classList.add(GAMES_SEARCH_MOBILE_OPEN));

  onOverLayClose = () => this.setState({ isOverlayOpen: false })

  render() {
    const {
      isUserLoggedIn,
      openGame,
      toggleFavoriteGame,
      producers,
      filters: {
        category: filterCategory,
        producers: filterProducers,
      },
      currentModal: { id },
      games,
      gamesFound,
      filteredByCategoryGames: filteredGames,
      showMore,
      isShowMoreAvailable,
      jackpotsIsInProgress,
      setProducers,
      clearAllFilters,
      closeModal,
      history,
      locale,
    } = this.props;

    const { isOpenSearch, isOverlayOpen, searchValue } = this.state;

    // const isAllGamesFilter = isAllGames(filterCategory);
    const isJackpotsFilter = filterCategory === GAME_CATEGORY_FILTER.JACKPOTS;

    if (!isUserLoggedIn && filterCategory === GAME_CATEGORY_FILTER.FAVORITE) {
      return <ChangeRoute to="/casino" />;
    }

    return (
      <>
        <div className={classNames('games-filters',
          { 'overlay-open d-md-none position-absolute w-100 min-vh-100 overflow-auto': isOverlayOpen },
          { 'position-relative': !isOverlayOpen },
          { 'producers-modal-open': id === MODAL_IDS.PRODUCERS })}
        >

          <div className={classNames('games-filters-overlay position-absolute d-none d-md-none bg-dark-black', { 'd-block': isOverlayOpen })} />

          <GamesFiltersMenu
            filterCategory={filterCategory}
            toggleProducers={this.toggleProducers}
            filterProducers={filterProducers}
            clearProducers={this.clearProducers}
            toggleSearchInput={this.toggleSearchInput}
            isOpenSearch={isOpenSearch}
            isUserLoggedIn={isUserLoggedIn}
            className={isOverlayOpen ? 'd-none' : ''}
          />

          <div className="d-md-none">
            <GamesSearchInput
              onChange={this.onChange}
              clearSearchValue={this.clearSearchValue}
              searchValue={searchValue}
              openProducersModal={this.openProducersModal}
              onClick={this.onOverlayOpen}
              isOverlayOpen={isOverlayOpen}
            />
          </div>

          <div className="d-none d-md-block px-sm-2_5 px-lg-4_75">
            {isOpenSearch && (
              <GamesSearchInput
                onChange={this.onChange}
                clearSearchValue={this.clearSearchValue}
                openProducersModal={this.openProducersModal}
                searchValue={searchValue}
                autoFocus
              />
            )}
          </div>

          {!!gamesFound && (
            <Producers
              filterCategory={filterCategory}
              producers={producers}
              filterProducers={filterProducers}
              setAllProducers={this.setAllProducers}
              setProducers={this.setProducers}
            />
          )}

          {(isJackpotsFilter && jackpotsIsInProgress) ? (
            <span />
          ) : (
            <>
              {filterCategory === GAME_CATEGORY_FILTER.ALL && (
                SUBCATEGORIES_BY_PAGE.GAMES.filter(item => item).map(subcategory => (
                  <SubcategoryGamesCarousel
                    coreProps={{ subcategory }}
                    className={classNames('mb-1_5 mb-sm-2_5 mb-lg-3_5', {
                      'd-none': (filterProducers.length || searchValue),
                    })}
                  />
                ))
              )}
              <div className={classNames('games-filters-list-wrapper w-100', { 'position-absolute': isOverlayOpen })}>
                {searchValue && (
                  <FormattedTag className="h2 text-uppercase d-block mx-2 mx-sm-2_5 mx-lg-5 mt-2_5" id="search-result" />
                )}

                {(filterCategory === GAME_CATEGORY_FILTER.ALL) && !searchValue && (
                  <FormattedTag
                    id="games.subcategory.all-games"
                    className="font-weight-bold letter-spacing-default text-uppercase games-title px-2 px-sm-2_5 px-lg-4_75 d-inline-block"
                  />
                )}

                <div
                  className={classNames('games-filters-list w-100 pb-2 pb-md-4_5 mt-2 mt-md-1 px-2 px-sm-2_5 px-lg-4_75', {
                    'd-block': !gamesFound,
                  })}
                >

                  {!isEmptyOrNil(games) && games.map((game, idx) => (
                    <GameCard
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${game.gameId}-${game.producer}-${idx}`}
                      {...game}
                      toggleFavoriteGame={toggleFavoriteGame}
                      openGame={openGame}
                      isUserLoggedIn={isUserLoggedIn}
                    />
                  ))}

                  {!gamesFound && (
                    <div className="d-flex flex-column align-items-center mt-4 mt-md-17_5">
                      <IconGamesNotFoundWithMark className="icon-games-not-found mb-2_75" />

                      <FormattedTag
                        id="search.results.not-found"
                        className="h2 text-uppercase mb-1_25 d-block"
                      />

                      <FormattedTag className="mx-2 text-center" id="search.results.not-found.advice" />
                    </div>
                  )}
                </div>

                {isShowMoreAvailable && (
                  <div className="px-2 px-sm-2_5 px-lg-5 pb-3 w-100 mb-3">
                    <FormattedTag
                      tag={Button}
                      id="games.show-more-games"
                      className="d-block mt-1 mx-auto"
                      color="primary"
                      onClick={showMore}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <ProducersModal
          filteredGames={filteredGames}
          producers={producers}
          filterProducers={filterProducers}
          setProducers={setProducers}
          clearAllFilters={clearAllFilters}
          closeModal={closeModal}
          history={history}
          locale={locale}
        />
      </>
    );
  }
}

export const GamesFilters = withCoreComponent(
  GamesFiltersCore,
  withRouter(
    withLocale(
      withModalActions(
        GamesFiltersUI,
      ),
    ),
  ),
);
